import { useVaultStatistics, useVaultDocuments, useStorageQuota } from '@/hooks/useVault';
import { DocumentType } from '@/types/vault.types';
import {
  BarChart3,
  Clock,
  Database,
  FileText,
  HardDrive,
  Receipt,
  Shield,
  TrendingUp,
  Upload,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface VaultStatisticsTabProps {
  vaultId: string;
}

export const VaultStatisticsTab = ({ vaultId }: VaultStatisticsTabProps) => {
  const { t } = useTranslation();
  const { data: statistics, isLoading } = useVaultStatistics(vaultId);
  const { data: documents } = useVaultDocuments(vaultId);
  const { data: quota } = useStorageQuota(vaultId);

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i] ?? 'KB'}`;
  };

  // Calculate document type distribution
  const getDocumentsByType = () => {
    const docs = documents || [];
    if (!docs.length) return [];

    const typeCounts: Record<string, number> = {};
    docs.forEach((doc) => {
      typeCounts[doc.type] = (typeCounts[doc.type] || 0) + 1;
    });

    const total = docs.length || 1;
    return Object.entries(typeCounts).map(([type, count]) => ({
      type,
      count,
      percentage: ((count / total) * 100).toFixed(1),
    }));
  };

  const getDocumentTypeIcon = (type: string) => {
    switch (type) {
      case DocumentType.PAYSLIP:
        return <Receipt className="h-5 w-5 text-green-600" />;
      case DocumentType.CONTRACT:
        return <FileText className="h-5 w-5 text-blue-600" />;
      case DocumentType.DSN:
        return <Database className="h-5 w-5 text-orange-600" />;
      case DocumentType.EDITION:
        return <BarChart3 className="h-5 w-5 text-amber-600" />;
      case DocumentType.OTHER:
        return <Shield className="h-5 w-5 text-purple-600" />;
      default:
        return <FileText className="h-5 w-5 text-neutral-600" />;
    }
  };

  const getDocumentTypeColor = (type: string) => {
    switch (type) {
      case DocumentType.PAYSLIP:
        return 'bg-green-500';
      case DocumentType.CONTRACT:
        return 'bg-blue-500';
      case DocumentType.DSN:
        return 'bg-orange-500';
      case DocumentType.EDITION:
        return 'bg-amber-500';
      case DocumentType.OTHER:
        return 'bg-purple-500';
      default:
        return 'bg-neutral-500';
    }
  };

  if (isLoading) {
    return (
      <div className="rounded-lg border border-neutral-200 bg-white p-6">
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
        </div>
      </div>
    );
  }

  const documentsByType = getDocumentsByType();
  const recentDocuments = documents?.slice(0, 5) || [];

  return (
    <div className="space-y-6">
      {/* Statistics Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <div className="rounded-lg border border-neutral-200 bg-white p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-50">
              <FileText className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-neutral-500">
                {t('vault.statistics.totalDocuments', 'Total Documents')}
              </p>
              <p className="text-2xl font-bold text-neutral-900">
                {statistics?.totalDocuments || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-neutral-200 bg-white p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-purple-50">
              <HardDrive className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-neutral-500">
                {t('vault.statistics.totalSize', 'Taille Totale')}
              </p>
              <p className="text-2xl font-bold text-neutral-900">
                {formatBytes(statistics?.totalSize || 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-neutral-200 bg-white p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-amber-50">
              <Clock className="h-6 w-6 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-neutral-500">
                {t('vault.statistics.lastActivity', 'Dernière Activité')}
              </p>
              <p className="text-sm font-semibold text-neutral-900">
                {statistics?.lastActivity
                  ? new Date(statistics.lastActivity).toLocaleDateString(
                    'fr-FR',
                  )
                  : t('common.never', 'Jamais')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Self-Upload Storage Quota */}
      {quota && (
        <div className="rounded-lg border border-neutral-200 bg-white p-6">
          <div className="mb-4 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50">
              <Upload className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-neutral-900">
                {t('vault.statistics.selfUploadQuota', 'Stockage personnel')}
              </h3>
              <p className="text-sm text-neutral-500">
                {t(
                  'vault.statistics.selfUploadQuotaDesc',
                  'Espace réservé à vos documents déposés manuellement',
                )}
              </p>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-end justify-between">
              <span className="text-sm text-neutral-600">
                {formatBytes(quota.used)}{' '}
                {t('vault.statistics.usedOf', 'utilisés sur')}{' '}
                {formatBytes(quota.limit)}
              </span>
              <span className={`text-sm font-semibold ${
                quota.percentage > 90
                  ? 'text-red-600'
                  : quota.percentage > 70
                    ? 'text-amber-600'
                    : 'text-emerald-600'
              }`}>
                {quota.percentage.toFixed(1)}%
              </span>
            </div>
            <div className="h-3 overflow-hidden rounded-full bg-neutral-100">
              <div
                className={`h-full rounded-full transition-all ${
                  quota.percentage > 90
                    ? 'bg-red-500'
                    : quota.percentage > 70
                      ? 'bg-amber-500'
                      : 'bg-emerald-500'
                }`}
                style={{ width: `${Math.min(100, quota.percentage)}%` }}
              />
            </div>
            <p className="text-xs text-neutral-500">
              {formatBytes(quota.remaining)}{' '}
              {t('vault.statistics.remainingSpace', 'restants')}
            </p>
          </div>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Documents by Type */}
        <div className="rounded-lg border border-neutral-200 bg-white p-6">
          <div className="mb-6 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-50">
              <BarChart3 className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-neutral-900">
                {t('vault.statistics.documentsByType', 'Documents par Type')}
              </h3>
              <p className="text-sm text-neutral-500">
                {t(
                  'vault.statistics.documentsByTypeDesc',
                  'Répartition des documents',
                )}
              </p>
            </div>
          </div>

          {documentsByType.length > 0 ? (
            <div className="space-y-4">
              {documentsByType.map(({ type, count, percentage }) => (
                <div key={type} className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      {getDocumentTypeIcon(type)}
                      <span className="font-medium text-neutral-700">
                        {type}
                      </span>
                    </div>
                    <span className="text-neutral-600">
                      {count} ({percentage}%)
                    </span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-neutral-100">
                    <div
                      className={`h-full ${getDocumentTypeColor(type)}`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <FileText className="mb-3 h-12 w-12 text-neutral-300" />
              <p className="text-sm text-neutral-500">
                {t('vault.statistics.noDocuments', 'Aucun document')}
              </p>
            </div>
          )}
        </div>

        {/* Recent Documents */}
        <div className="rounded-lg border border-neutral-200 bg-white p-6">
          <div className="mb-6 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-50">
              <TrendingUp className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-neutral-900">
                {t('vault.statistics.recentDocuments', 'Documents Récents')}
              </h3>
              <p className="text-sm text-neutral-500">
                {t(
                  'vault.statistics.recentDocumentsDesc',
                  '5 derniers documents',
                )}
              </p>
            </div>
          </div>

          {recentDocuments.length > 0 ? (
            <div className="space-y-3">
              {recentDocuments.map((doc) => (
                <div
                  key={doc.uuid}
                  className="flex items-center gap-3 rounded-lg border border-neutral-200 p-3 transition-colors hover:bg-neutral-50"
                >
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-neutral-100">
                    {getDocumentTypeIcon(doc.type)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-neutral-900">
                      {doc.originalFilename}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-neutral-500">
                      <span>{doc.type}</span>
                      <span>•</span>
                      <span>{formatBytes(doc.originalSize)}</span>
                      <span>•</span>
                      <span>
                        {new Date(doc.createdAt).toLocaleDateString('fr-FR')}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <FileText className="mb-3 h-12 w-12 text-neutral-300" />
              <p className="text-sm text-neutral-500">
                {t('vault.statistics.noRecentDocuments', 'Aucun document récent')}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Storage Info */}
      <div className="rounded-lg border border-neutral-200 bg-gradient-to-br from-primary-50 to-purple-50 p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-white shadow-sm">
              <Shield className="h-6 w-6 text-primary-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-neutral-900">
                {t('vault.statistics.storageInfo', 'Informations de Stockage')}
              </h3>
              <p className="text-sm text-neutral-600">
                {t(
                  'vault.statistics.storageInfoDesc',
                  'Chiffrement AES-256-GCM • Date de rétention configurée',
                )}
              </p>
            </div>
          </div>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <div className="rounded-lg bg-white p-3 shadow-sm">
            <p className="text-xs text-neutral-500">
              {t('vault.statistics.encryptionMethod', 'Méthode')}
            </p>
            <p className="mt-1 text-sm font-semibold text-neutral-900">
              AES-256-GCM
            </p>
          </div>
          <div className="rounded-lg bg-white p-3 shadow-sm">
            <p className="text-xs text-neutral-500">
              {t('vault.statistics.storageType', 'Type')}
            </p>
            <p className="mt-1 text-sm font-semibold text-neutral-900">
              Zero-Knowledge
            </p>
          </div>
          <div className="rounded-lg bg-white p-3 shadow-sm">
            <p className="text-xs text-neutral-500">
              {t('vault.statistics.retentionPeriod', 'Rétention')}
            </p>
            <p className="mt-1 text-sm font-semibold text-neutral-900">
              50 ans / 75 ans
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
